import { useMemo, useRef, useState } from "react";
import { Search, Plus, Check, X } from "lucide-react";

const norm = (s) => (s || "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

// 314: výběr dodavatele v objednávce — pole s lupou, píšeš název nebo IČO a vybereš ze seznamu.
// Kdo v seznamu není, jde rovnou přidat („+ Nový dodavatel“ otevře kartu s ARES).
export function SupplierPicker({ suppliers, name, supplierId, onPick, onType, onNew, invalid, testid = "order-supplier" }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const blurT = useRef(null);
  const q = norm(name);
  const picked = suppliers.find((s) => s.id === supplierId);
  const list = useMemo(() => {
    const all = q && !(picked && norm(picked.name) === q)
      ? suppliers.filter((s) => norm(s.name).includes(q) || (s.ico || "").includes(q.replace(/\s/g, "")))
      : suppliers;
    return all.slice(0, 50);
  }, [suppliers, q, picked]);
  const exact = suppliers.some((s) => norm(s.name) === q);
  const rows = [...list.map((s) => ({ s })), ...(onNew ? [{ add: true }] : [])];

  const choose = (r) => {
    setOpen(false);
    if (!r) return;
    if (r.add) onNew(exact ? "" : name.trim());
    else onPick(r.s);
  };
  const key = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && open) { e.preventDefault(); choose(rows[hi]); }
    else if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); }
  };

  return (
    <div className="relative min-w-0" onBlur={() => { blurT.current = setTimeout(() => setOpen(false), 120); }} onFocus={() => clearTimeout(blurT.current)}>
      <div className={`h-11 w-full flex items-center gap-2 rounded-md border bg-background px-3 focus-within:ring-2 focus-within:ring-ring ${invalid ? "border-destructive" : "border-input"}`}>
        <Search size={16} className="text-muted-foreground shrink-0" />
        <input value={name} onChange={(e) => { onType(e.target.value); setOpen(true); setHi(0); }} onFocus={() => setOpen(true)} onKeyDown={key}
          placeholder="Hledat dodavatele nebo IČO" autoComplete="off" role="combobox" aria-expanded={open} aria-autocomplete="list"
          className="flex-1 min-w-0 bg-transparent outline-none text-base md:text-sm" data-testid={testid} />
        {picked && <Check size={16} className="text-success shrink-0" aria-label="Vybraný dodavatel" />}
        {name && <button type="button" onClick={() => { onType(""); setOpen(true); }} aria-label="Smazat" className="text-muted-foreground hover:text-foreground shrink-0"><X size={15} /></button>}
      </div>
      {open && (
        <ul role="listbox" className="absolute z-50 left-0 right-0 mt-1 max-h-[260px] overflow-y-auto rounded-[14px] border border-border bg-popover shadow-lg p-1" data-testid={`${testid}-list`}>
          {rows.map((r, i) => r.add ? (
            <li key="add" role="option" aria-selected={hi === i}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(r)} onMouseEnter={() => setHi(i)} data-testid={`${testid}-new`}
                className={`w-full min-h-[44px] px-2.5 rounded-[10px] text-left inline-flex items-center gap-2 text-[14px] font-bold pp-ink ${hi === i ? "bg-brand/10" : ""}`}>
                <Plus size={16} className="shrink-0" />{name.trim() && !exact ? `Nový dodavatel „${name.trim()}“` : "Nový dodavatel"}
              </button>
            </li>
          ) : (
            <li key={r.s.id} role="option" aria-selected={hi === i}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(r)} onMouseEnter={() => setHi(i)} data-testid={`${testid}-opt-${r.s.id}`}
                className={`w-full min-h-[44px] px-2.5 py-1.5 rounded-[10px] text-left ${hi === i ? "bg-secondary" : ""}`}>
                <span className="block text-[14px] font-semibold truncate">{r.s.name}</span>
                {(r.s.ico || r.s.city || r.s.orders_total) ? (
                  <span className="block text-[12px] text-muted-foreground truncate">
                    {[r.s.ico && `IČO ${r.s.ico}`, r.s.city, r.s.orders_total ? `${r.s.orders_total}× objednáno` : null].filter(Boolean).join(" · ")}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
          {!list.length && !onNew && <li className="px-2.5 py-3 text-sm text-muted-foreground">Nic nenalezeno.</li>}
        </ul>
      )}
    </div>
  );
}
